import { type } from 'arktype';
import { createPaginatedResponseSchema } from '../shared/pagination';

/**
 * Los tres enumerados propios de "experienceJoin" (documento 05a, §5.3). Mismo estilo que
 * `contracts/experiences`: constante exportada (`as const`) + unión de literales ArkType
 * (`type.enumerated(...)`).
 */

export const JOIN_STATUSES = [
	'REQUESTED',
	'WAITLISTED',
	'CONFIRMED',
	'ATTENDED',
	'NO_SHOW',
	'CANCELLED',
	'REJECTED',
] as const;
export const joinStatusSchema = type.enumerated(...JOIN_STATUSES);
export type JoinStatus = typeof joinStatusSchema.infer;

export const EXPERIENCE_COLLABORATOR_SUBROLES = ['FACILITATOR', 'HOST', 'PROVIDER', 'MONITOR', 'HELPER'] as const;
export const experienceCollaboratorSubroleSchema = type.enumerated(...EXPERIENCE_COLLABORATOR_SUBROLES);
export type ExperienceCollaboratorSubrole = typeof experienceCollaboratorSubroleSchema.infer;

export const LUNES_MARTES_TEMPLATES = ['ENCAJA', 'NEUTRO', 'NEGATIVO'] as const;
export const lunesMartesTemplateSchema = type.enumerated(...LUNES_MARTES_TEMPLATES);
export type LunesMartesTemplate = typeof lunesMartesTemplateSchema.infer;

/**
 * Las diez opciones de "¿qué puedes aportar?" (documento 05b §10.5): vocabulario del cliente,
 * igual que `LunesMartesTemplate` — no un catálogo de la base, la columna
 * "experienceJoin"."aventureroSubrole" es TEXT libre (documento 05a §5.3, DDL literal).
 */
export const AVENTURERO_SUBROLES = [
	'COCINILLAS',
	'FOTOGRAFO',
	'ECO_AMIGABLE',
	'DJ_MANANERO',
	'EXPLORADOR',
	'SOCIAL_BUTTERFLY',
	'INTREPIDO',
	'FILOSOFO_VIAJERO',
	'MINIMALISTA',
	'MIMOSO_DEL_SUENO',
] as const;
export const aventureroSubroleSchema = type.enumerated(...AVENTURERO_SUBROLES);
export type AventureroSubrole = typeof aventureroSubroleSchema.infer;

/**
 * Los cinco desenlaces de intentar unirse (documento 05a §6.2, §6.7). `blocked` es una
 * DESVIACIÓN DELIBERADA sobre el PDF: el §6.2 solo declara tres desenlaces ("entra", "lista de
 * espera", "redirección"), pero el documento 05b §10.1 exige una pantalla propia de "bloqueado
 * por ausencias" y CL-9 del documento 05a obliga a comprobar el bloqueo en el servidor, no solo
 * en la pantalla. Sin un cuarto valor, un bloqueo tendría que disfrazarse de una de las otras
 * tres salidas, y ninguna encaja: no es un `redirect` (no hay a dónde redirigir, el problema es
 * temporal) ni un `join`/`waitlist`.
 *
 * `requested` es la quinta salida de `POST /experiences/:id/join`, introducida por la
 * iteración 06 (centro de mando): en una experiencia oficial, obtener hueco ya no da plaza
 * directamente — deja una solicitud pendiente de que quien modera la acepte.
 */
export const JOIN_OUTCOMES = ['join', 'waitlist', 'redirect', 'blocked', 'requested'] as const;
export const joinOutcomeSchema = type.enumerated(...JOIN_OUTCOMES);
export type JoinOutcome = typeof joinOutcomeSchema.infer;

/** Los cuatro destinos de una redirección (documento 05a §6.7; documento 05b §10.2). */
export const REDIRECT_TARGETS = ['createAccount', 'membershipGate', 'invitationRequired', 'recommendations'] as const;
export const redirectTargetSchema = type.enumerated(...REDIRECT_TARGETS);
export type RedirectTarget = typeof redirectTargetSchema.infer;

/**
 * El motivo en código de la decisión de unirse (documento 05a §6.7: "el navegador lo traduce;
 * la API no manda texto humano"). El frontend lo traduce vía i18n (`namespace joins,
 * motivos.<clave>`); la API nunca devuelve texto para humanos.
 */
export const JOIN_REASONS = [
	'planPrivadoSinInvitacion',
	'sinCuenta',
	'soloParaMiembros',
	'hayPlaza',
	'plazasGuardadasParaNuevos',
	'aforoCompleto',
	'bloqueadoPorAusencias',
	'yaTienesPlaza',
] as const;
export const joinReasonSchema = type.enumerated(...JOIN_REASONS);
export type JoinReason = typeof joinReasonSchema.infer;

/** Por qué cuesta lo que cuesta unirse (documento 05a §6.5). Claves, no texto para humanos. */
export const JOIN_COST_REASONS = [
	'primeraGratis',
	'cubiertaPorMembresia',
	'cuotaPlanGrumete',
	'precioPropioDelPlan',
	'sinCoste',
] as const;
export const joinCostReasonSchema = type.enumerated(...JOIN_COST_REASONS);
export type JoinCostReason = typeof joinCostReasonSchema.infer;

/**
 * Cuánto cuesta unirse, por qué, y si desaparecería aceptando una invitación pendiente
 * (documento 05a §6.7, §6.5; documento 05b §10.4). `gratisConInvitacion` e `invitadoPor` solo
 * viajan cuando aplica la comparación del segundo plan del grumete.
 */
export const joinCostSchema = type({
	cents: 'number.integer >= 0',
	currency: 'string',
	'gratisConInvitacion?': 'boolean',
	'invitadoPor?': 'string',
	motivo: joinCostReasonSchema,
}).onUndeclaredKey('delete');

export type JoinCost = typeof joinCostSchema.infer;

/**
 * `POST /experiences/:id/join` (documento 05a §6.7): el identificador de la experiencia va en
 * la URL. `aventureroSubrole` es opcional porque "¿qué puedes aportar?" solo se pregunta si el
 * organizador activó `askContribution` en esa experiencia (documento 05b §10.5); `preSensation`
 * recoge "cómo llegaba" para alimentar el ADN computado (documento 05a §5.1).
 */
export const createJoinSchema = type({
	'aventureroSubrole?': aventureroSubroleSchema,
	'preSensation?': 'string',
}).onUndeclaredKey('delete');

export type CreateJoin = typeof createJoinSchema.infer;

/**
 * `DELETE /experiences/:id/join` (documento 05a §6.7): avisar de que no vas. `motivo` es
 * opcional (decisión D-31, documento 05b §15: motivo opcional que el organizador puede marcar
 * como justificado — hoy solo se guarda, sin validación de administración).
 */
export const cancelJoinSchema = type({
	'motivo?': 'string',
}).onUndeclaredKey('delete');

export type CancelJoin = typeof cancelJoinSchema.infer;

/**
 * La respuesta de `POST /experiences/:id/join` (documento 05a §6.7): la decisión, nunca un
 * error (RN-14, documento 05a decisión 7 — ninguna redirección es un error, así que las cuatro
 * salidas viajan con 200). `waLink` SOLO viaja con plaza (RN-9): en cualquier otro desenlace
 * está ausente, nunca vacío ni null. `blockedUntil` solo acompaña a `outcome: 'blocked'`.
 */
export const joinDecisionResponseSchema = type({
	'blockedUntil?': 'string',
	cost: joinCostSchema,
	outcome: joinOutcomeSchema,
	reason: joinReasonSchema,
	'redirectTo?': redirectTargetSchema,
	'waitlistPosition?': 'number.integer >= 1',
	'waLink?': 'string',
}).onUndeclaredKey('delete');

export type JoinDecisionResponse = typeof joinDecisionResponseSchema.infer;

/**
 * `PATCH /experience-joins/:id` (documento 05a §6.7, `GET/POST/PATCH/DELETE
 * /experience-joins`): lo único que la propia persona posee de su participación —ni el estado,
 * ni el rol congelado, ni nada que decida el organizador.
 */
export const updateExperienceJoinSchema = type({
	'aventureroSubrole?': aventureroSubroleSchema,
	'postSensation?': 'string',
	'preSensation?': 'string',
}).onUndeclaredKey('delete');

export type UpdateExperienceJoin = typeof updateExperienceJoinSchema.infer;

/**
 * La fila propia de una participación (documento 05a §5.1, §6.7: `GET /experience-joins`,
 * filtro por dueño). Todo salvo `id`/`experienceId`/`userId`/`status` es opcional porque cada
 * columna puede no tener valor todavía (una `REQUESTED` recién creada no tiene `joinedAt` ni
 * `attendedAt`). Fechas como `'string.date.iso'`, mismo criterio que `experienceResponseSchema`.
 */
export const experienceJoinResponseSchema = type({
	'attendedAt?': 'string.date.iso | null',
	'aventureroSubrole?': aventureroSubroleSchema,
	'cancelledAt?': 'string.date.iso | null',
	'collaboratorSubrole?': experienceCollaboratorSubroleSchema,
	createdAt: 'string.date.iso',
	experienceId: 'string.uuid',
	'funnelStateAtJoin?': 'string | null',
	id: 'string.uuid',
	'joinedAt?': 'string.date.iso | null',
	'paidAmountCents?': 'number | null',
	'postSensation?': 'string | null',
	'preSensation?': 'string | null',
	status: joinStatusSchema,
	'tierAtJoin?': 'string | null',
	updatedAt: 'string.date.iso',
	userId: 'string.uuid',
}).onUndeclaredKey('delete');

export type ExperienceJoinResponse = typeof experienceJoinResponseSchema.infer;

export const experienceJoinPaginatedResponseSchema = createPaginatedResponseSchema(experienceJoinResponseSchema);
export type ExperienceJoinPaginatedResponse = typeof experienceJoinPaginatedResponseSchema.infer;

/**
 * "Quién va" (documento 05a §6.7 «la ficha redactada»; documento 05b §10.6 «el tablón»):
 * JAMÁS teléfono ni redes sociales (CA-05b.10). `avatarUrl` y `fullName` nullable porque el
 * perfil de origen ya los declara así (`userProfileResponseSchema`).
 */
export const participantResponseSchema = type({
	'avatarUrl?': 'string | null',
	'aventureroSubrole?': aventureroSubroleSchema,
	'fullName?': 'string | null',
	id: 'string.uuid',
	// El sello ⚓ de la comunidad histórica (ver el JSDoc de `isOldSchool` en
	// `meResponseSchema`, `contracts/users`): mismo criterio de opcionalidad que
	// `avatarUrl`/`fullName`, sin `| null` — la columna en base no es nullable, así que ausente
	// solo significa "no lo lleva", nunca "sin dato".
	'isOldSchool?': 'boolean',
}).onUndeclaredKey('delete');

export type ParticipantResponse = typeof participantResponseSchema.infer;

/**
 * El contacto de un participante (documento 05a «la ficha redactada»: fila "contacto de
 * participantes", solo para el organizador o administración). Nunca se devuelve junto al
 * listado público de "quién va" — es un DTO propio, con los mismos campos que
 * `participantResponseSchema` más los dos de contacto, escrito a mano y no derivado de él
 * (mismo criterio que el resto de este fichero: cada operación declara su propia forma).
 *
 * `whatsapp` SALIÓ de este contrato (respuestas del cliente 2026-09-17, P-31: «Teléfono: nadie,
 * nunca, en ninguna pantalla [...] Ni en el perfil, ni en la ficha del plan, ni detrás de un "ver
 * teléfono"») y en su lugar viaja `whatsappLink`: el `wa.me` ya resuelto, con el mensaje
 * pre-escrito («quién eres y a qué plan vais»). El servidor arma el enlace a partir del número
 * (que nunca sale de él); si no hay número, no hay enlace, y el campo simplemente queda ausente.
 */
export const participantContactResponseSchema = type({
	'avatarUrl?': 'string | null',
	'aventureroSubrole?': aventureroSubroleSchema,
	'fullName?': 'string | null',
	id: 'string.uuid',
	'instagram?': 'string | null',
	// Mismo campo y mismo criterio que `participantResponseSchema.isOldSchool`, ver ese JSDoc.
	'isOldSchool?': 'boolean',
	'whatsappLink?': 'string',
}).onUndeclaredKey('delete');

export type ParticipantContactResponse = typeof participantContactResponseSchema.infer;

/** `GET /experiences/:id/participants` (documento 05a §6.7): la lista de quién va. */
export const participantsResponseSchema = type({
	items: participantResponseSchema.array(),
}).onUndeclaredKey('delete');

export type ParticipantsResponse = typeof participantsResponseSchema.infer;
